import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { StatusStamp } from "@/components/StatusStamp";
import { DocumentDetailModal } from "@/components/DocumentDetailModal";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"];
const MONTH_NAMES = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

export default function CalendarPage() {
  const [cursor, setCursor] = useState(new Date());
  const [counts, setCounts] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDocs, setDayDocs] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;

  useEffect(() => {
    api
      .get("/documents/calendar", { params: { month: monthStr } })
      .then(({ data }) => {
        const map = {};
        data.forEach((x) => {
          map[x.date] = x;
        });
        setCounts(map);
      })
      .catch(() => {});
  }, [monthStr]);

  const openDay = async (dateStr) => {
    setSelectedDay(dateStr);
    try {
      const { data } = await api.get("/documents", { params: { date_from: dateStr, date_to: dateStr } });
      setDayDocs(data);
    } catch {
      setDayDocs([]);
    }
  };

  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(startOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <div data-testid="calendar-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl font-bold text-slate-900">Календарь документов</h1>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev-month-button">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium text-slate-700 w-36 text-center" data-testid="calendar-current-month">
            {MONTH_NAMES[month]} {year}
          </span>
          <Button size="icon" variant="outline" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next-month-button">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-center text-xs font-semibold text-slate-400 py-1">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={`empty-${i}`} />;
            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const info = counts[dateStr];
            const incoming = info?.incoming_companies || [];
            const outgoing = info?.outgoing_companies || [];
            return (
              <button
                key={dateStr}
                type="button"
                onClick={() => openDay(dateStr)}
                data-testid={`calendar-day-button-${dateStr}`}
                className={`min-h-[108px] border rounded-lg p-1.5 text-left hover:border-blue-300 hover:bg-blue-50/50 transition-colors flex flex-col gap-1 overflow-hidden ${
                  selectedDay === dateStr ? "border-blue-400 bg-blue-50" : "border-slate-100"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-600">{d}</span>
                  {info && (
                    <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 rounded px-1" data-testid={`calendar-day-count-${dateStr}`}>
                      {info.total}
                    </span>
                  )}
                </div>
                {incoming.length > 0 && (
                  <div className="space-y-0.5" data-testid={`calendar-day-incoming-${dateStr}`}>
                    {incoming.slice(0, 2).map((name) => (
                      <div key={name} className="flex items-center gap-1 text-[9px] leading-tight text-emerald-700 truncate">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                        <span className="truncate">{name}</span>
                      </div>
                    ))}
                    {incoming.length > 2 && <div className="text-[9px] text-emerald-600 pl-2.5">+{incoming.length - 2} вх.</div>}
                  </div>
                )}
                {outgoing.length > 0 && (
                  <div className="space-y-0.5" data-testid={`calendar-day-outgoing-${dateStr}`}>
                    {outgoing.slice(0, 2).map((name) => (
                      <div key={name} className="flex items-center gap-1 text-[9px] leading-tight text-blue-700 truncate">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                        <span className="truncate">{name}</span>
                      </div>
                    ))}
                    {outgoing.length > 2 && <div className="text-[9px] text-blue-600 pl-2.5">+{outgoing.length - 2} исх.</div>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500" data-testid="calendar-legend">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Входящие — кто прислал</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Исходящие — кому отправили</span>
      </div>

      {selectedDay && (
        <div className="mt-5 bg-white border border-slate-200 rounded-xl p-4" data-testid="calendar-day-panel">
          <div className="text-sm font-semibold text-slate-700 mb-3">Документы за {selectedDay}</div>
          <div className="space-y-2">
            {dayDocs.map((doc) => (
              <div
                key={doc.id}
                onClick={() => setSelectedDoc(doc)}
                data-testid={`calendar-doc-row-${doc.id}`}
                className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-100"
              >
                <span className="text-slate-800">{doc.type} №{doc.number}</span>
                <StatusStamp status={doc.status} />
              </div>
            ))}
            {dayDocs.length === 0 && <div className="text-center text-slate-400 text-xs py-6">В этот день документов нет</div>}
          </div>
        </div>
      )}

      {selectedDoc && (
        <DocumentDetailModal
          docId={selectedDoc.id}
          open={!!selectedDoc}
          onOpenChange={(v) => !v && setSelectedDoc(null)}
          onChanged={() => openDay(selectedDay)}
        />
      )}
    </div>
  );
}
